import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Sparkles, Info } from "lucide-react";
import { STORAGE_OPTIONS, fmtStorage } from "@/lib/catalogMeta";

const InfoTip = ({ text, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="ml-1.5 text-[#002FA7] hover:text-black"
        aria-label="info"
      >
        <Info size={13} />
      </button>
      {open && (
        <span className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 bg-[#0A0A0A] text-white text-[11px] leading-snug p-3 normal-case font-sans font-normal tracking-normal shadow-hard">
          {text}
        </span>
      )}
    </span>
  );
};

export const ProductFields = ({ vf, onCategoryChange }) => {
  const { t, lang } = useLang();
  const {
    form, update, onBrandChange, categories, brands, models, modelMeta,
    hasBrands, hasModels, brandIsOther, modelIsOther, resolvedBrand,
    hasStorage, hasSize, isVehicle, isMotorVehicle,
  } = vf;

  const groups = categories.reduce((acc, c) => {
    const key = lang === "it" ? c.group_it : c.group_en;
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="lg:col-span-7 bg-white border border-black p-8">
      <div className="flex items-center justify-between mb-6">
        <span className="overline">01 / {t.valuator.section_basics}</span>
        <span className="font-mono text-xs text-[#737373]">REQUIRED</span>
      </div>

      <div className="space-y-5">
        <div>
          <label className="label-rv">{t.valuator.category}</label>
          <select data-testid="form-category" name="category" className="input-rv" value={form.category} onChange={(e) => onCategoryChange(e.target.value)}>
            {Object.entries(groups).map(([g, items]) => (
              <optgroup key={g} label={g}>
                {items.map((c) => <option key={c.key} value={c.key}>{lang === "it" ? c.it : c.en}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className="label-rv">{t.valuator.brand}</label>
          {hasBrands ? (
            <select
              data-testid="form-brand"
              name="brand"
              className="input-rv"
              value={form.brand}
              onChange={(e) => onBrandChange(e.target.value)}
              disabled={!form.category}
            >
              <option value="">— {t.valuator.brand_select} —</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              <option value="__other__">{t.valuator.other_option}</option>
            </select>
          ) : (
            <input
              data-testid="form-brand"
              name="brand"
              className="input-rv"
              value={form.brand_custom}
              onChange={(e) => update("brand_custom", e.target.value)}
              placeholder={form.category ? "Apple, Sony..." : t.valuator.brand_placeholder}
              disabled={!form.category}
            />
          )}
          {brandIsOther && (
            <input
              data-testid="form-brand-custom"
              className="input-rv mt-2"
              value={form.brand_custom}
              onChange={(e) => update("brand_custom", e.target.value)}
              placeholder={t.valuator.custom_brand_placeholder}
            />
          )}
        </div>

        <div>
          <label className="label-rv">{t.valuator.model}</label>
          {hasModels ? (
            <select
              data-testid="form-model"
              name="model"
              className="input-rv"
              value={form.model}
              onChange={(e) => update("model", e.target.value)}
              disabled={!resolvedBrand}
            >
              <option value="">— {t.valuator.model_select} —</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
              <option value="__other__">{t.valuator.other_option}</option>
            </select>
          ) : (
            <input
              data-testid="form-model"
              name="model"
              className="input-rv"
              value={form.model_custom}
              onChange={(e) => update("model_custom", e.target.value)}
              placeholder={resolvedBrand ? "iPhone 13 128GB..." : t.valuator.brand_placeholder}
              disabled={!resolvedBrand}
            />
          )}
          {modelIsOther && (
            <input
              data-testid="form-model-custom"
              className="input-rv mt-2"
              value={form.model_custom}
              onChange={(e) => update("model_custom", e.target.value)}
              placeholder={t.valuator.custom_model_placeholder}
            />
          )}
          {modelMeta && (
            <div data-testid="model-info-hint" className="mt-2 bg-[#FFCC00]/20 border border-black/20 px-3 py-2 flex items-center gap-2">
              <Sparkles size={13} className="text-[#002FA7] flex-shrink-0" />
              <span className="font-mono text-[10px] tracking-wide uppercase">
                {t.valuator.model_autofill}: {t.valuator.release_year} {modelMeta.release_year} · {t.valuator.list_price} €{modelMeta.msrp}
              </span>
            </div>
          )}
        </div>

        {hasStorage && (
          <div data-testid="storage-block">
            <label className="label-rv">{t.valuator.storage}</label>
            <select data-testid="form-storage" className="input-rv" value={form.storage_gb} onChange={(e) => update("storage_gb", e.target.value)}>
              <option value="">{t.valuator.optional_select}</option>
              {STORAGE_OPTIONS.map((gb) => <option key={gb} value={gb}>{fmtStorage(gb)}</option>)}
            </select>
          </div>
        )}

        {hasSize && (
          <div data-testid="size-block">
            <label className="label-rv">{t.valuator.item_size}</label>
            <input data-testid="form-item-size" className="input-rv" value={form.item_size} onChange={(e) => update("item_size", e.target.value)} placeholder={t.valuator.item_size_ph} />
          </div>
        )}

        <div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-rv">{t.valuator.purchase_price} <InfoTip testid="tooltip-purchase-price" text={t.valuator.price_tooltip_purchase} /></label>
              <input data-testid="form-purchase-price" name="purchase_price" type="number" step="0.01" min="0" className="input-rv text-gray-900" value={form.purchase_price} onChange={(e) => update("purchase_price", e.target.value)} placeholder={t.valuator.purchase_price_ph} />
            </div>
            <div>
              <label className="label-rv">{t.valuator.original_retail} <InfoTip testid="tooltip-retail-price" text={t.valuator.price_tooltip_retail} /></label>
              <input data-testid="form-retail-price" name="original_retail_price" type="number" step="0.01" min="0" className="input-rv text-gray-900" value={form.original_retail_price} onChange={(e) => update("original_retail_price", e.target.value)} placeholder={t.valuator.retail_price_ph} />
            </div>
          </div>
          {!(parseFloat(form.purchase_price) > 0 || parseFloat(form.original_retail_price) > 0) && (
            <p data-testid="price-or-hint" className="font-mono text-[10px] font-bold uppercase tracking-widest text-[#FF3B30] mt-2">
              {t.valuator.price_or_hint}
            </p>
          )}
        </div>

        {isVehicle && (
          <div className={isMotorVehicle ? "grid grid-cols-2 gap-4" : ""} data-testid="kilometers-block">
            <div>
              <label className="label-rv">{t.valuator.kilometers}</label>
              <input data-testid="form-kilometers" name="kilometers" type="number" min="0" className="input-rv" value={form.kilometers} onChange={(e) => update("kilometers", e.target.value)} placeholder="85000" />
              <p className="text-[11px] text-[#737373] mt-1">{t.valuator.kilometers_help}</p>
            </div>
            {isMotorVehicle && (
              <div data-testid="engine-cc-block">
                <label className="label-rv">{t.valuator.engine_cc}</label>
                <input data-testid="form-engine-cc" type="number" min="0" className="input-rv" value={form.engine_cc} onChange={(e) => update("engine_cc", e.target.value)} placeholder="1600" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductFields;
